import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle2, ArrowRight, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';

const Confirmation = () => {
  const [searchParams] = useSearchParams();
  const [serviceName, setServiceName] = useState('');
  const [price, setPrice] = useState('');

  useEffect(() => {
    setServiceName(searchParams.get('service') || 'Service');
    setPrice(searchParams.get('price') || '0');
  }, [searchParams]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-teal-50">
      {/* Header */}
      <header className="bg-white/95 backdrop-blur-sm border-b border-gray-200">
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <Link to="/">
              <img
                src="https://customer-assets.emergentagent.com/job_5fbc50c9-4bd7-40ac-b8aa-d9e061478b31/artifacts/ita9gf9x_ApplyLikeAPro-Logo-Navy.png"
                alt="Apply Like a Pro"
                className="h-12 w-auto"
              />
            </Link>
          </div>
        </nav>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
          {/* Success Header */}
          <div className="bg-gradient-to-r from-teal-600 to-teal-700 px-8 py-12 text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-white rounded-full mb-6">
              <CheckCircle2 className="text-teal-700" size={48} />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Thank You for Your Purchase!
            </h1>
            <p className="text-teal-100 text-lg">
              Your order has been successfully processed
            </p>
          </div>

          {/* Order Details */}
          <div className="px-8 py-8">
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Order Summary</h2>
              <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <div className="text-sm text-gray-600 mb-1">Service Purchased</div>
                    <div className="text-xl font-bold text-gray-900">{serviceName}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm text-gray-600 mb-1">Amount Paid</div>
                    <div className="text-2xl font-bold text-teal-700">${price}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* What's Next */}
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">What Happens Next?</h2>
              <div className="space-y-4">
                <div className="flex items-start space-x-4 p-4 bg-teal-50 rounded-lg border border-teal-200">
                  <div className="w-10 h-10 bg-teal-700 rounded-lg flex items-center justify-center text-white font-bold flex-shrink-0">
                    1
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Check Your Email</h3>
                    <p className="text-gray-700 text-sm">
                      You'll receive a confirmation email with your order details and next steps within the next few minutes.
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4 p-4 bg-amber-50 rounded-lg border border-amber-200">
                  <div className="w-10 h-10 bg-amber-600 rounded-lg flex items-center justify-center text-white font-bold flex-shrink-0">
                    2
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Complete Your Intake Survey</h3>
                    <p className="text-gray-700 text-sm">
                      We'll send you a detailed intake survey to gather information about your job search goals, experience, and preferences.
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="w-10 h-10 bg-blue-700 rounded-lg flex items-center justify-center text-white font-bold flex-shrink-0">
                    3
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">We Get to Work</h3>
                    <p className="text-gray-700 text-sm">
                      Our team will begin customizing your résumés and submitting applications on your behalf. Standard turnaround is 3-5 business days per application pack. You'll receive regular updates throughout the process.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Info */}
            <div className="bg-gradient-to-br from-gray-50 to-slate-100 rounded-xl p-6 border border-gray-200">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Mail className="text-teal-700" size={24} />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Questions or Need Help?</h3>
                  <p className="text-gray-700 mb-3">
                    Our team is here to support you every step of the way. Don't hesitate to reach out if you have any questions.
                  </p>
                  <a
                    href="mailto:shelly@applylikeapro.org"
                    className="text-teal-700 hover:text-teal-800 font-semibold inline-flex items-center"
                  >
                    shelly@applylikeapro.org
                    <ArrowRight className="ml-2" size={16} />
                  </a>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <Link to="/" className="flex-1">
                <Button
                  variant="outline"
                  className="w-full border-2 border-gray-300 hover:border-teal-700 hover:text-teal-700"
                >
                  Return to Home
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* Additional Note */}
        <div className="mt-8 text-center">
          <p className="text-gray-600">
            This is a confirmation page. You will receive manual follow-up from our team shortly.
          </p>
        </div>
      </main>
    </div>
  );
};

export default Confirmation;